import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tenantsApi, apiKeysApi } from '../../services/api';
import { EditIcon, UsersIcon, TrashIcon, PlusIcon, CopyIcon, KeyIcon, CheckIcon } from '../../utils/icons';
import { formatDate } from '../../utils/formatters';
import Modal from '../../components/Modal';
import './TenantDetail.css';

export default function TenantDetail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Activate
  const [activating, setActivating] = useState(false);

  // Deactivate modal
  const [showDeactivate, setShowDeactivate] = useState(false);

  // API Keys
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [showGenerateKey, setShowGenerateKey] = useState(false);
  const [showRevokeKey, setShowRevokeKey] = useState(null);

  const fetchTenant = useCallback(async () => {
    try {
      setError('');
      const res = await tenantsApi.get(tenantId);
      setTenant(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Organisation not found.');
      } else {
        setError('Failed to load organisation.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoadingKeys(true);
      const res = await apiKeysApi.list(tenantId);
      setApiKeys(res.data);
    } catch {
      // Non-critical — keys section will show empty
    } finally {
      setLoadingKeys(false);
    }
  }, [tenantId]);

  useEffect(() => { fetchTenant(); fetchApiKeys(); }, [fetchTenant, fetchApiKeys]);

  const startEditing = () => {
    setForm({
      name: tenant.name,
      address: tenant.address,
      email: tenant.email,
      contact: tenant.contact,
      max_users: tenant.max_users,
      is_active: tenant.is_active,
      leads_module_enabled: tenant.leads_module_enabled,
      booking_module_enabled: tenant.booking_module_enabled,
      sales_module_enabled: tenant.sales_module_enabled,
    });
    setSaveError('');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setForm(null);
    setSaveError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await tenantsApi.update(tenantId, form);
      const res = await tenantsApi.get(tenantId);
      setTenant(res.data);
      setEditing(false);
      setForm(null);
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    setActivating(true);
    try {
      await tenantsApi.update(tenantId, { is_active: true });
      await fetchTenant();
    } catch (err) {
      setSaveError(err.response?.data?.detail || 'Failed to activate.');
    } finally {
      setActivating(false);
    }
  };

  /* ─── Loading ─────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="td-page">
        <div className="td-loading">
          <div className="td-loading-spinner" />
          <p>Loading organisation…</p>
        </div>
      </div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="td-page">
        <div className="td-error-page">
          <p>{error}</p>
          <button className="btn btn-ghost" onClick={() => navigate('/super-admin/tenants')}>
            Back to Organisations
          </button>
        </div>
      </div>
    );
  }

  /* ─── Main Render ─────────────────────────────────────────────────────── */

  return (
    <div className="td-page">
      {/* Breadcrumb */}
      <nav className="tu-breadcrumb animate-fade-up">
        <Link to="/super-admin/tenants" className="tu-breadcrumb-link">Organisations</Link>
        <span className="tu-breadcrumb-sep">/</span>
        <span className="tu-breadcrumb-current">{tenant.name}</span>
      </nav>

      {/* Header */}
      <div className="page-header animate-fade-up" style={{ animationDelay: '40ms' }}>
        <div className="page-header-top">
          <div>
            <p className="section-label" style={{ marginBottom: 4 }}>Organisation Details</p>
            <h1 className="page-title">{tenant.name}</h1>
            <p className="page-subtitle">{tenant.slug}.realtycrm.com</p>
          </div>
          <div className="page-actions">
            {editing ? (
              <>
                <button className="btn btn-ghost" onClick={cancelEditing} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-ghost"
                  onClick={() => navigate(`/super-admin/tenants/${tenantId}/users`)}
                >
                  <UsersIcon /> Manage Users
                </button>
                <button className="btn btn-primary" onClick={startEditing}>
                  <EditIcon /> Edit
                </button>
                {tenant.is_active ? (
                  <button className="btn btn-danger" onClick={() => setShowDeactivate(true)}>
                    <TrashIcon /> Deactivate
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleActivate} disabled={activating}>
                    {activating ? 'Activating…' : 'Activate'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detail / Edit Card */}
      <div className="animate-fade-up" style={{ animationDelay: '80ms' }}>
        {editing ? (
          <div className="card td-card">
            {saveError && <div className="td-save-error">{saveError}</div>}

            <div className="td-edit-grid">
              {/* Name */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="section-label">Name</label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Organisation name"
                  required
                />
              </div>

              {/* Slug — read-only */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="section-label">Subdomain</label>
                <div className="td-readonly-value">{tenant.slug}.realtycrm.com</div>
              </div>

              {/* Address — full width */}
              <div className="form-group td-edit-full" style={{ marginBottom: 0 }}>
                <label className="section-label">Address</label>
                <input
                  className="input"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Organisation address"
                  required
                />
              </div>

              {/* Email */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="section-label">Email</label>
                <input
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="contact@org.com"
                  required
                />
              </div>

              {/* Contact */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="section-label">Contact</label>
                <input
                  className="input"
                  type="tel"
                  value={form.contact}
                  onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                  placeholder="+91 98765 43210"
                  required
                />
              </div>

              {/* Max Users */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="section-label">Max Users</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={500}
                  value={form.max_users}
                  onChange={(e) => setForm((f) => ({ ...f, max_users: parseInt(e.target.value) || 1 }))}
                  required
                />
              </div>

              {/* Status */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="section-label">Status</label>
                <div className="toggle-row">
                  <ToggleSwitch
                    checked={form.is_active}
                    onChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                  />
                  <span className="toggle-label">{form.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              <div className="td-edit-divider" />

              {/* Modules */}
              <div className="td-edit-modules">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="section-label">Leads Module</label>
                  <div className="toggle-row">
                    <ToggleSwitch
                      checked={form.leads_module_enabled}
                      onChange={(v) => setForm((f) => ({ ...f, leads_module_enabled: v }))}
                    />
                    <span className="toggle-label">{form.leads_module_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="section-label">Booking Module</label>
                  <div className="toggle-row">
                    <ToggleSwitch
                      checked={form.booking_module_enabled}
                      onChange={(v) => setForm((f) => ({ ...f, booking_module_enabled: v }))}
                    />
                    <span className="toggle-label">{form.booking_module_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="section-label">Sales Module</label>
                  <div className="toggle-row">
                    <ToggleSwitch
                      checked={form.sales_module_enabled}
                      onChange={(v) => setForm((f) => ({ ...f, sales_module_enabled: v }))}
                    />
                    <span className="toggle-label">{form.sales_module_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              </div>

              <div className="td-edit-divider" />

              {/* Dates — read-only */}
              <div>
                <label className="section-label">Created</label>
                <div className="td-readonly-value">{formatDate(tenant.created_at)}</div>
              </div>
              <div>
                <label className="section-label">Last Updated</label>
                <div className="td-readonly-value">{formatDate(tenant.updated_at)}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card td-card">
            <div className="td-grid">
              {/* Name */}
              <div>
                <span className="section-label td-field-label">Name</span>
                <div className="td-field-value">{tenant.name}</div>
              </div>

              {/* Subdomain */}
              <div>
                <span className="section-label td-field-label">Subdomain</span>
                <div className="td-field-value td-field-value--mono">{tenant.slug}.realtycrm.com</div>
              </div>

              {/* Address — full width */}
              <div className="td-field-full">
                <span className="section-label td-field-label">Address</span>
                <div className="td-field-value">{tenant.address}</div>
              </div>

              {/* Email */}
              <div>
                <span className="section-label td-field-label">Email</span>
                <div className="td-field-value td-field-value--secondary">{tenant.email}</div>
              </div>

              {/* Contact */}
              <div>
                <span className="section-label td-field-label">Contact</span>
                <div className="td-field-value td-field-value--secondary">{tenant.contact}</div>
              </div>

              {/* Max Users */}
              <div>
                <span className="section-label td-field-label">Max Users</span>
                <div className="td-field-value">{tenant.max_users}</div>
              </div>

              {/* Status */}
              <div>
                <span className="section-label td-field-label">Status</span>
                <div style={{ marginTop: 2 }}>
                  <span className={`badge badge-${tenant.is_active ? 'closed' : 'lost'}`}>
                    <span className="badge-dot" />
                    {tenant.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="td-divider" />

              {/* Modules */}
              <div className="td-modules-row">
                <div className="td-module-item">
                  <span className={`td-module-dot ${tenant.leads_module_enabled ? 'td-module-dot--on' : 'td-module-dot--off'}`} />
                  <span className="td-module-name">Leads Module</span>
                  <span className={`td-module-status ${tenant.leads_module_enabled ? 'td-module-status--on' : 'td-module-status--off'}`}>
                    {tenant.leads_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="td-module-item">
                  <span className={`td-module-dot ${tenant.booking_module_enabled ? 'td-module-dot--on' : 'td-module-dot--off'}`} />
                  <span className="td-module-name">Booking Module</span>
                  <span className={`td-module-status ${tenant.booking_module_enabled ? 'td-module-status--on' : 'td-module-status--off'}`}>
                    {tenant.booking_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="td-module-item">
                  <span className={`td-module-dot ${tenant.sales_module_enabled ? 'td-module-dot--on' : 'td-module-dot--off'}`} />
                  <span className="td-module-name">Sales Module</span>
                  <span className={`td-module-status ${tenant.sales_module_enabled ? 'td-module-status--on' : 'td-module-status--off'}`}>
                    {tenant.sales_module_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="td-divider" />

              {/* Dates */}
              <div>
                <span className="section-label td-field-label">Created</span>
                <div className="td-field-value td-field-value--secondary">{formatDate(tenant.created_at)}</div>
              </div>
              <div>
                <span className="section-label td-field-label">Last Updated</span>
                <div className="td-field-value td-field-value--secondary">{formatDate(tenant.updated_at)}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API Keys Section */}
      {!editing && (
        <div className="animate-fade-up" style={{ animationDelay: '120ms' }}>
          <div className="card td-card td-keys-section">
            <div className="td-keys-header">
              <div className="td-keys-title">
                <KeyIcon /> <span>API Keys</span>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowGenerateKey(true)}>
                <PlusIcon /> Generate Key
              </button>
            </div>

            {loadingKeys ? (
              <div className="td-keys-loading">Loading keys…</div>
            ) : apiKeys.length === 0 ? (
              <div className="td-keys-empty">
                No API keys have been generated for this organisation yet.
              </div>
            ) : (
              <table className="td-keys-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((k) => (
                    <tr key={k.id} className="td-key-row">
                      <td className="td-key-name">{k.name}</td>
                      <td className="td-key-date">{formatDate(k.created_at)}</td>
                      <td className="td-key-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Revoke key"
                          onClick={() => setShowRevokeKey(k)}
                        >
                          <TrashIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Generate Key Modal */}
      {showGenerateKey && (
        <GenerateKeyModal
          tenantId={tenantId}
          onClose={() => setShowGenerateKey(false)}
          onGenerated={() => fetchApiKeys()}
        />
      )}

      {/* Revoke Key Modal */}
      {showRevokeKey && (
        <RevokeKeyModal
          tenantId={tenantId}
          apiKey={showRevokeKey}
          onClose={() => setShowRevokeKey(null)}
          onRevoked={() => { setShowRevokeKey(null); fetchApiKeys(); }}
        />
      )}

      {/* Deactivate confirmation */}
      {showDeactivate && (
        <DeactivateModal
          tenant={tenant}
          onClose={() => setShowDeactivate(false)}
          onConfirm={async () => {
            await tenantsApi.delete(tenantId);
            navigate('/super-admin/tenants');
          }}
        />
      )}
    </div>
  );
}


/* ─── Generate Key Modal ───────────────────────────────────────────────── */

function GenerateKeyModal({ tenantId, onClose, onGenerated }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiKeysApi.create(tenantId, { name: name.trim() });
      setGeneratedKey(res.data.api_key);
      onGenerated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate API key.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text
      const input = document.querySelector('.td-key-raw-input');
      if (input) { input.select(); document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    }
  };

  if (generatedKey) {
    return (
      <Modal title="API Key Generated" size="sm" onClose={onClose}>
        <div className="modal-body">
          <p className="td-key-warning">
            Copy this key now — it will not be shown again.
          </p>
          <div className="td-key-raw-group">
            <label className="section-label">API Key</label>
            <div className="td-key-raw-row">
              <input
                className="input td-key-raw-input"
                value={generatedKey}
                readOnly
                onClick={(e) => e.target.select()}
              />
              <button className="btn btn-ghost btn-icon" onClick={handleCopy} title="Copy">
                {copied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Generate API Key" size="sm" onClose={onClose}>
      <form onSubmit={handleGenerate}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}
          <div className="form-group">
            <label className="section-label">Key Name <span style={{ color: 'var(--danger)' }}>*</span></label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 99acres Integration"
              maxLength={100}
              autoFocus
              required
            />
            <p className="td-key-hint">A descriptive name to identify this key's purpose.</p>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
            {saving ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </form>
    </Modal>
  );
}


/* ─── Revoke Key Modal ─────────────────────────────────────────────────── */

function RevokeKeyModal({ tenantId, apiKey, onClose, onRevoked }) {
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');

  const handleRevoke = async () => {
    setRevoking(true);
    setError('');
    try {
      await apiKeysApi.revoke(tenantId, apiKey.id);
      onRevoked();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke API key.');
      setRevoking(false);
    }
  };

  return (
    <Modal title="Revoke API Key" size="sm" onClose={onClose}>
      <div className="modal-body">
        {error && <div className="modal-error">{error}</div>}
        <p className="delete-msg">
          Are you sure you want to revoke <strong>{apiKey.name}</strong>?
        </p>
        <p className="delete-hint">
          Any integrations using this key will stop working immediately.
        </p>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-danger" onClick={handleRevoke} disabled={revoking}>
          {revoking ? 'Revoking…' : 'Revoke'}
        </button>
      </div>
    </Modal>
  );
}


/* ─── Deactivate Confirmation Modal ─────────────────────────────────────── */

function DeactivateModal({ tenant, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      await onConfirm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to deactivate.');
      setDeleting(false);
    }
  };

  return (
    <Modal title="Deactivate Organisation" size="sm" onClose={onClose}>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}
          <p className="delete-msg">
            Are you sure you want to deactivate <strong>{tenant.name}</strong>?
          </p>
          <p className="delete-hint">
            The organisation and its data will be preserved but users will no longer be able to log in.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-danger" onClick={handleConfirm} disabled={deleting}>
            {deleting ? 'Deactivating…' : 'Deactivate'}
          </button>
        </div>
    </Modal>
  );
}


/* ─── Toggle Switch ─────────────────────────────────────────────────────── */

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`toggle-switch ${checked ? 'toggle-switch--on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-thumb" />
    </button>
  );
}


